import { useMemo } from 'react';
import {
  primaryNavItems,
  secondaryNavItems,
} from '../../../shared/components/layout/navigationItems';
import {
  flattenNavItems,
  searchNavItems,
  filterByPermissions,
  deduplicateNavItems,
  filterOutParentItems,
  SearchableNavItem,
} from '../utils/navigationSearch';

const MAX_RESULTS = 8;

/**
 * Hook to compute command palette search results
 *
 * @param query - Search query string
 * @param permissions - User's permission strings
 * @returns Filtered and sorted search results
 */
export function useCommandPaletteSearch(
  query: string,
  permissions: string[]
): SearchableNavItem[] {
  // Flatten and prepare all navigation items with permission filtering
  // Filter out parent items FIRST, before any other processing
  const allNavItems = useMemo(() => {
    const flattened = [
      ...flattenNavItems(primaryNavItems),
      ...flattenNavItems(secondaryNavItems),
    ];
    // Remove parent items immediately (Workforce, Labour, etc.)
    // Children keep their parent context in the `.parent` field for subtitle display
    const withoutParents = filterOutParentItems(flattened);
    const deduplicated = deduplicateNavItems(withoutParents);
    return filterByPermissions(deduplicated, permissions);
  }, [permissions]);

  // Compute search results (parents already filtered out)
  const results = useMemo(() => {
    let items: SearchableNavItem[];

    if (!query.trim()) {
      // No query: show all items (up to MAX_RESULTS)
      items = allNavItems;
    } else {
      // With query: search and filter
      items = searchNavItems(query, allNavItems);
    }

    // Limit results (no need to filter parents again)
    return items.slice(0, MAX_RESULTS);
  }, [query, allNavItems]);

  return results;
}
